import type { ReactNode, ReactElement } from 'react';
import { Component } from 'react';

import { logError } from '@web-platform/shared-universal-utils-frontend/src/helpers/logger';
import { getErrorBoundaryStackMessage } from '@web-platform/shared-universal-utils-universal/src/error-boundary-stack-message/get-error-boundary-stack-message';

import type { ReactHandleErrorInfo } from '@web-platform/shared-universal-types/src/react';
import type { Nullable } from '@web-platform/shared-universal-types-utils/src/utils';

type Props = {
  children: ReactElement;
  componentName: string;
  fallback: Nullable<ReactElement>;
  onError?: (arg0: { component: string; error: string }) => void;
};
type State = {
  hasError: boolean;
};

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
    };
  }

  componentDidCatch(error: Error, info: ReactHandleErrorInfo) {
    const { componentName, onError } = this.props;

    this.setState({
      hasError: true,
    });

    if (onError) {
      // onError overrides any logging in this component
      onError({
        component: componentName,
        error: error.toString(),
      });
    } else {
      logError({
        component: componentName,
        error,
        message: 'Error caught by error boundary',
        stack: getErrorBoundaryStackMessage(error, info),
      });
    }
  }

  render(): ReactNode {
    const { hasError } = this.state;
    const { children, componentName, fallback, onError } = this.props;

    if (!hasError) {
      return children;
    }
    return fallback !== null ? (
      <ErrorBoundary
        // This error boundary catches the case that the fallback throws an error.
        // It will then render nothing and log the error (or call `onError` if provided).
        componentName={`${componentName}-fallback`}
        fallback={null}
        onError={onError}
      >
        {fallback}
      </ErrorBoundary>
    ) : null;
  }
}

export default ErrorBoundary;
